import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { X } from 'lucide-react';
import { useContent } from 'context/ContentContext';

// Mobile full-screen overlay
const MobileOverlay = styled.div`
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: flex-end;
  animation: ${({ isOpen }) => (isOpen ? 'fadeIn 0.2s ease' : 'none')};

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  // Hide on desktop - use modal instead
  ${({ theme }) => theme.mediaQueries.md} {
    display: none;
  }
`;

const MobileSheet = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  width: 100%;
  max-height: 80vh;
  border-radius: 20px 20px 0 0;
  padding: 1.5rem;
  overflow-y: auto;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

// Desktop modal overlay
const DesktopOverlay = styled.div`
  display: none;

  // Only show as modal on desktop
  ${({ theme }) => theme.mediaQueries.md} {
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  }

  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
`;

const DesktopModalContent = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 2rem;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const ModalTitle = styled.h3`
  ${({ theme }) => theme.mixins.textP2};
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.black};
  font-weight: 700;
  padding-right: 2rem;
`;

const SheetHandle = styled.div`
  width: 40px;
  height: 4px;
  background-color: ${({ theme }) => theme.colors.greyLight};
  border-radius: 2px;
  margin: 0 auto 1.5rem;
`;

const RemoveVariantsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const RemoveVariantItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.offWhite};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
`;

const VariantName = styled.span`
  ${({ theme }) => theme.mixins.textP3};
  color: ${({ theme }) => theme.colors.black};
  font-weight: 600;
`;

const VariantQuantity = styled.span`
  ${({ theme }) => theme.mixins.textP3};
  color: ${({ theme }) => theme.colors.darkGrey};
  margin-left: 0.5rem;
`;

const RemoveVariantButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  min-height: 44px;
  box-shadow: ${({ theme }) =>
    `0 6px 20px ${theme.colors.primary}30, inset 0 1px 0 rgba(255,255,255,0.12)`};

  ${({ theme }) => theme.mediaQueries.sm} {
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${({ theme }) => `0 8px 24px ${theme.colors.primary}40`};
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

const ConfirmButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${({ theme }) => `${theme.colors.darkGrey}14`};
  color: ${({ theme }) => theme.colors.darkGrey};
  border: 1.5px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 700;
  letter-spacing: 0.03em;
  min-height: 48px;

  ${({ theme }) => theme.mediaQueries.sm} {
    &:hover {
      background: ${({ theme }) => theme.colors.offWhite};
      border-color: ${({ theme }) => theme.colors.grey};
      transform: translateY(-1px);
    }
  }
`;

const ConfirmButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 700;
  letter-spacing: 0.03em;
  min-height: 48px;
  box-shadow: ${({ theme }) =>
    `0 6px 20px ${theme.colors.primary}30, inset 0 1px 0 rgba(255,255,255,0.12)`};

  ${({ theme }) => theme.mediaQueries.sm} {
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${({ theme }) => `0 8px 24px ${theme.colors.primary}40`};
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

const CloseModalButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.black};
  display: flex;
  align-items: center;
  justify-content: center;
`;

/**
 * Component for the removal modal/drawer
 */
const RemovalModal = ({
  isRemoveDrawerOpen,
  isRemoveModalOpen,
  isRemoveAllDrawerOpen,
  isRemoveAllModalOpen,
  closeRemoveModal,
  closeRemoveAllModal,
  removeVariant,
  removeAllVariants,
  variantsInCart,
  item,
}) => {
  const { messages } = useContent();

  // Nothing is open: skip mounting the portal into document.body entirely.
  if (
    !isRemoveDrawerOpen &&
    !isRemoveModalOpen &&
    !isRemoveAllDrawerOpen &&
    !isRemoveAllModalOpen
  ) {
    return null;
  }

  const modalContent = (
    <>
      {/* Mobile Remove Sheet */}
      <MobileOverlay isOpen={isRemoveDrawerOpen} onClick={closeRemoveModal}>
        <MobileSheet onClick={(e) => e.stopPropagation()}>
          <SheetHandle />
          <ModalTitle>
            {messages?.modal?.selectItemToRemove || 'Select item to remove'}
          </ModalTitle>
          <RemoveVariantsList>
            {variantsInCart.map((variant) => (
              <RemoveVariantItem key={variant.id}>
                <div>
                  <VariantName>{variant.name}</VariantName>
                  <VariantQuantity>x{variant.quantity}</VariantQuantity>
                </div>
                <RemoveVariantButton onClick={() => removeVariant(variant.id)}>
                  {messages?.modal?.remove || 'Remove'}
                </RemoveVariantButton>
              </RemoveVariantItem>
            ))}
          </RemoveVariantsList>
          <ConfirmButtons>
            <CancelButton onClick={closeRemoveModal}>
              {messages?.modal?.cancel || 'Cancel'}
            </CancelButton>
          </ConfirmButtons>
        </MobileSheet>
      </MobileOverlay>

      {/* Desktop Remove Modal */}
      <DesktopOverlay isOpen={isRemoveModalOpen} onClick={closeRemoveModal}>
        <DesktopModalContent onClick={(e) => e.stopPropagation()}>
          <CloseModalButton onClick={closeRemoveModal} aria-label="Close modal">
            <X size={24} />
          </CloseModalButton>
          <ModalTitle>
            {messages?.modal?.selectItemToRemove || 'Select item to remove'}
          </ModalTitle>
          <RemoveVariantsList>
            {variantsInCart.map((variant) => (
              <RemoveVariantItem key={variant.id}>
                <div>
                  <VariantName>{variant.name}</VariantName>
                  <VariantQuantity>x{variant.quantity}</VariantQuantity>
                </div>
                <RemoveVariantButton onClick={() => removeVariant(variant.id)}>
                  {messages?.modal?.remove || 'Remove'}
                </RemoveVariantButton>
              </RemoveVariantItem>
            ))}
          </RemoveVariantsList>
          <ConfirmButtons>
            <CancelButton onClick={closeRemoveModal}>
              {messages?.modal?.cancel || 'Cancel'}
            </CancelButton>
          </ConfirmButtons>
        </DesktopModalContent>
      </DesktopOverlay>

      {/* Mobile Remove All Sheet */}
      <MobileOverlay
        isOpen={isRemoveAllDrawerOpen}
        onClick={closeRemoveAllModal}
      >
        <MobileSheet onClick={(e) => e.stopPropagation()}>
          <SheetHandle />
          <ModalTitle>
            {messages?.modal?.removeAll || 'Remove all'} {item.name}?
          </ModalTitle>
          <RemoveVariantsList>
            {variantsInCart.map((variant) => (
              <RemoveVariantItem key={variant.id}>
                <div>
                  <VariantName>{variant.name}</VariantName>
                  <VariantQuantity>x{variant.quantity}</VariantQuantity>
                </div>
              </RemoveVariantItem>
            ))}
          </RemoveVariantsList>
          <ConfirmButtons>
            <CancelButton onClick={closeRemoveAllModal}>
              {messages?.modal?.cancel || 'Cancel'}
            </CancelButton>
            <ConfirmButton onClick={removeAllVariants}>
              {messages?.modal?.removeAll || 'Remove All'}
            </ConfirmButton>
          </ConfirmButtons>
        </MobileSheet>
      </MobileOverlay>

      {/* Desktop Remove All Modal */}
      <DesktopOverlay
        isOpen={isRemoveAllModalOpen}
        onClick={closeRemoveAllModal}
      >
        <DesktopModalContent onClick={(e) => e.stopPropagation()}>
          <CloseModalButton
            onClick={closeRemoveAllModal}
            aria-label="Close modal"
          >
            <X size={24} />
          </CloseModalButton>
          <ModalTitle>
            {messages?.modal?.removeAll || 'Remove all'} {item.name}?
          </ModalTitle>
          <RemoveVariantsList>
            {variantsInCart.map((variant) => (
              <RemoveVariantItem key={variant.id}>
                <div>
                  <VariantName>{variant.name}</VariantName>
                  <VariantQuantity>x{variant.quantity}</VariantQuantity>
                </div>
              </RemoveVariantItem>
            ))}
          </RemoveVariantsList>
          <ConfirmButtons>
            <CancelButton onClick={closeRemoveAllModal}>
              {messages?.modal?.cancel || 'Cancel'}
            </CancelButton>
            <ConfirmButton onClick={removeAllVariants}>
              {messages?.modal?.removeAll || 'Remove All'}
            </ConfirmButton>
          </ConfirmButtons>
        </DesktopModalContent>
      </DesktopOverlay>
    </>
  );

  // Use portal to render outside of component hierarchy
  return createPortal(modalContent, document.body);
};

RemovalModal.propTypes = {
  isRemoveDrawerOpen: PropTypes.bool.isRequired,
  isRemoveModalOpen: PropTypes.bool.isRequired,
  isRemoveAllDrawerOpen: PropTypes.bool.isRequired,
  isRemoveAllModalOpen: PropTypes.bool.isRequired,
  closeRemoveModal: PropTypes.func.isRequired,
  closeRemoveAllModal: PropTypes.func.isRequired,
  removeVariant: PropTypes.func.isRequired,
  removeAllVariants: PropTypes.func.isRequired,
  variantsInCart: PropTypes.array.isRequired,
  item: PropTypes.object.isRequired,
};

export default RemovalModal;
